import { RouteContext } from '@lobby/contexts/RouteContext';
import { appErrorSelector } from '@lobby/store/selectors/app';
import {
  configLoadedSelector,
  definitionsSelector,
} from '@lobby/store/selectors/config';
import { socket } from '@lobby/store/socket';
import { sendHeightMessage } from '@lobby/utils/postMessage';
import {
  Suspense,
  useEffect,
  lazy,
  useCallback,
  useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
} from 'react-router';

import { usePrevious } from '@ezugi/hooks/usePrevious';

import { locale } from './store/locale';

import '@ezugi/polyfills';

import 'modern-normalize';
import './styles.css';

const Home = lazy(() => import('./pages/Home'));
const Error = lazy(() => import('./pages/Error'));

const rootElement = document.getElementById('root');

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const previousHeight = usePrevious(
    rootElement?.firstElementChild?.scrollHeight
  );
  const navigate = useNavigate();

  const { basename, setBasename } = useContext(RouteContext);
  const definitions = useSelector(definitionsSelector);
  const configLoaded = useSelector(configLoadedSelector);
  const { code } = useSelector(appErrorSelector) || {};

  // Set basename if necessary & redirect to it
  useEffect(() => {
    if (!!definitions?.LINK_PREFIX && basename !== definitions?.LINK_PREFIX) {
      setBasename(definitions.LINK_PREFIX);
      navigate(`${definitions.LINK_PREFIX}/${window.location.href}`);
    }
  }, [ definitions, basename, setBasename, navigate ]);

  const toggleLocalization = useCallback(() => {
    dispatch(locale.toggle());
  }, [ dispatch ]);

  useEffect(() => {
    window.TOGGLE_LOCALIZATION = toggleLocalization;
  }, [ toggleLocalization ]);

  // Send Height PostMessage on route change
  useEffect(() => {
    const currentHeight = rootElement?.firstElementChild?.scrollHeight;

    if (previousHeight !== currentHeight) {
      sendHeightMessage(currentHeight);
    }
  }, [ previousHeight, location ]);

  // Close socket connection on unmount
  useEffect(() => () => dispatch(socket.close()), [ dispatch ]);

  if (!configLoaded && !code) return null;

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/error/:code" element={<Error />} />
        <Route path="/*" element={<Home />} />
      </Routes>
    </Suspense>
  );
};

export default App;
